import React, { useState } from "react";

import {
  Button,
  Typography,
  Snackbar,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tabs,
  Tab,
  Box,
} from "@material-ui/core";

import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { uploadLeadsExcel, createManualLead } from "views/Apis/AfterLogin.js"; // Update this path to your API file

const useStyles = makeStyles((theme) => ({
  ...styles,
  dropZone: {
    border: "2px dashed #ccc",
    padding: "30px",
    textAlign: "center",
    cursor: "pointer",
    color: "#888",
    marginBottom: "20px",
    backgroundColor: "#fafafa",
  },
  input: {
    display: "none",
  },
  icon: {
    fontSize: "40px",
    marginBottom: "10px",
    color: "#00acc1",
  },
  formField: {
    marginBottom: "16px",
    width: "100%",
  },
  submitButton: {
    marginTop: "20px",
    width: "100%",
  },
  tabContent: {
    padding: "20px 0",
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box className={props.className}>{children}</Box>}
    </div>
  );
}

export default function Bulkupload() {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);

  // Bulk upload states
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Manual entry states
  const [manualLead, setManualLead] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    source: "MANUAL",
    companyType: "INDIVIDUAL",
    companyName: "",
    location: "",
    alternativeContact: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Common states
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "info",
    message: ""
  });

  // Tab handling
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Bulk upload functions
  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const result = await uploadLeadsExcel(selectedFile);
      setUploadResult(result);
      setSnackbar({
        open: true,
        severity: "success",
        message: `Upload Successful: ${result.saved_records} saved, ${result.skipped_duplicates} duplicates.`,
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: error.message,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      setFile(droppedFile);
      handleFileUpload(droppedFile);
      event.dataTransfer.clearData();
    }
  };

  const handleFileSelect = (event) => {
    const selected = event.target.files[0];
    setFile(selected);
    handleFileUpload(selected);
  };

  // Manual entry functions
  const handleManualInputChange = (field) => (event) => {
    setManualLead({
      ...manualLead,
      [field]: event.target.value,
    });
  };

  const validateManualLead = () => {
    const requiredFields = ['name', 'email', 'phoneNumber', 'address', 'companyName', 'location'];
    for (let field of requiredFields) {
      if (!manualLead[field].trim()) {
        return `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualLead.email)) {
      return "Please enter a valid email address";
    }

    // Phone validation (basic)
    if (manualLead.phoneNumber.length < 10) {
      return "Phone number must be at least 10 digits";
    }

    return null;
  };

  const handleManualSubmit = async () => {
    const validationError = validateManualLead();
    if (validationError) {
      setSnackbar({
        open: true,
        severity: "error",
        message: validationError,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createManualLead(manualLead);

      // Check if the API response indicates success
      if (result.status) {
        setSnackbar({
          open: true,
          severity: "success",
          message: result.message || "Lead created successfully!",
        });

        // Reset form on success
        setManualLead({
          name: "",
          email: "",
          phoneNumber: "",
          address: "",
          source: "MANUAL",
          companyType: "INDIVIDUAL",
          companyName: "",
          location: "",
          alternativeContact: "",
        });
      } else {
        // API returned success HTTP status but with status: false
        setSnackbar({
          open: true,
          severity: "error",
          message: result.message || "Failed to create lead",
        });
      }
    } catch (error) {
      // Handle HTTP errors and network issues
      let errorMessage = "Failed to create lead";

      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = "Invalid data provided. Please check your inputs.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="info">
              <h4 className={classes.cardTitleWhite}>Lead Management</h4>
              <p className={classes.cardCategoryWhite}>
                Upload leads via Excel file or add manually
              </p>
            </CardHeader>
            <CardBody>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Bulk Upload" icon={<CloudUploadIcon />} />
                <Tab label="Manual Entry" icon={<PersonAddIcon />} />
              </Tabs>

              {/* Bulk Upload Tab */}
              <TabPanel value={tabValue} index={0} className={classes.tabContent}>
                <Paper
                  className={classes.dropZone}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <CloudUploadIcon className={classes.icon} />
                  <Typography variant="body1">
                    {isUploading ? "Uploading..." : "Drag & drop Excel file here, or click to select"}
                  </Typography>
                  <input
                    className={classes.input}
                    id="file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                  <label htmlFor="file-input">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      style={{ marginTop: "10px" }}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Choose File"}
                    </Button>
                  </label>
                </Paper>

                {file && (
                  <Typography variant="body2">
                    Selected file: <strong>{file.name}</strong>
                  </Typography>
                )}

                {uploadResult && (
                  <div style={{ marginTop: "20px" }}>
                    <Typography variant="body2">
                      <strong>Status:</strong> {uploadResult.status}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Records:</strong> {uploadResult.total_records}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Saved Records:</strong> {uploadResult.saved_records}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Skipped Duplicates:</strong> {uploadResult.skipped_duplicates}
                    </Typography>
                  </div>
                )}
              </TabPanel>

              {/* Manual Entry Tab */}
              <TabPanel value={tabValue} index={1} className={classes.tabContent}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name *"
                      value={manualLead.name}
                      onChange={handleManualInputChange('name')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email *"
                      type="email"
                      value={manualLead.email}
                      onChange={handleManualInputChange('email')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number *"
                      value={manualLead.phoneNumber}
                      onChange={handleManualInputChange('phoneNumber')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Alternative Contact"
                      value={manualLead.alternativeContact}
                      onChange={handleManualInputChange('alternativeContact')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Address *"
                      value={manualLead.address}
                      onChange={handleManualInputChange('address')}
                      className={classes.formField}
                      variant="outlined"
                      multiline
                      rows={2}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" className={classes.formField}>
                      <InputLabel>Company Type *</InputLabel>
                      <Select
                        value={manualLead.companyType}
                        onChange={handleManualInputChange('companyType')}
                        label="Company Type *"
                        disabled={isSubmitting}
                      >
                        <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                        <MenuItem value="COMPANY">Company</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name *"
                      value={manualLead.companyName}
                      onChange={handleManualInputChange('companyName')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Location *"
                      value={manualLead.location}
                      onChange={handleManualInputChange('location')}
                      className={classes.formField}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" className={classes.formField}>
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={manualLead.source}
                        onChange={handleManualInputChange('source')}
                        label="Source"
                        disabled={isSubmitting}
                      >
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="WEBSITE">Website</MenuItem>
                        <MenuItem value="LINKEDIN">LinkedIn</MenuItem>
                        <MenuItem value="INSTAGRAM">Instagram</MenuItem>
                        <MenuItem value="FACEBOOK">Facebook</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleManualSubmit}
                      className={classes.submitButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Creating Lead..." : "Create Lead"}
                    </Button>
                  </Grid>
                </Grid>
              </TabPanel>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}






































// import React, { useState } from "react";



// import {
//   Button,
//   Typography,
//   Snackbar,
//   Paper,
// } from "@material-ui/core";


// import MuiAlert from "@material-ui/lab/Alert";

// import { makeStyles } from "@material-ui/core/styles";
// import CloudUploadIcon from "@material-ui/icons/CloudUpload";

// import GridContainer from "components/Grid/GridContainer.js";
// import GridItem from "components/Grid/GridItem.js";
// import Card from "components/Card/Card.js";
// import CardHeader from "components/Card/CardHeader.js";
// import CardBody from "components/Card/CardBody.js";

// import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
// import { uploadLeadsExcel } from "views/Apis/AfterLogin.js"; // Update this path to your API file

// const useStyles = makeStyles((theme) => ({
//   ...styles,
//   dropZone: {
//     border: "2px dashed #ccc",
//     padding: "30px",
//     textAlign: "center",
//     cursor: "pointer",
//     color: "#888",
//     marginBottom: "20px",
//     backgroundColor: "#fafafa",
//   },
//   input: {
//     display: "none",
//   },
//   icon: {
//     fontSize: "40px",
//     marginBottom: "10px",
//     color: "#00acc1",
//   },
// }));

// function Alert(props) {
//   return <MuiAlert elevation={6} variant="filled" {...props} />;
// }

// export default function Bulkupload() {
//   const classes = useStyles();
//   const [file, setFile] = useState(null);
//   const [uploadResult, setUploadResult] = useState(null);
//   const [isUploading, setIsUploading] = useState(false);
//   const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

//   const handleFileUpload = async (selectedFile) => {
//     if (!selectedFile) return;

//     setIsUploading(true);
//     setUploadResult(null);

//     try {
//       const result = await uploadLeadsExcel(selectedFile);
//       setUploadResult(result);
//       setSnackbar({
//         open: true,
//         severity: "success",
//         message: `Upload Successful: ${result.saved_records} saved, ${result.skipped_duplicates} duplicates.`,
//       });
//     } catch (error) {
//       setSnackbar({
//         open: true,
//         severity: "error",
//         message: error.message,
//       });
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   const handleDrop = (event) => {
//     event.preventDefault();
//     if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
//       const droppedFile = event.dataTransfer.files[0];
//       setFile(droppedFile);
//       handleFileUpload(droppedFile);
//       event.dataTransfer.clearData();
//     }
//   };

//   const handleFileSelect = (event) => {
//     const selected = event.target.files[0];
//     setFile(selected);
//     handleFileUpload(selected);
//   };

//   const handleCloseSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   return (
//     <div>
//       <GridContainer>
//         <GridItem xs={12}>
//           <Card>
//             <CardHeader color="info">
//               <h4 className={classes.cardTitleWhite}>Bulk Upload Leads</h4>
//               <p className={classes.cardCategoryWhite}>
//                 Upload leads via Excel file (.xlsx or .xls)
//               </p>
//             </CardHeader>
//             <CardBody>
//               <Paper
//                 className={classes.dropZone}
//                 onDrop={handleDrop}
//                 onDragOver={(e) => e.preventDefault()}
//               >
//                 <CloudUploadIcon className={classes.icon} />
//                 <Typography variant="body1">
//                   {isUploading ? "Uploading..." : "Drag & drop Excel file here, or click to select"}
//                 </Typography>
//                 <input
//                   className={classes.input}
//                   id="file-input"
//                   type="file"
//                   accept=".xlsx,.xls"
//                   onChange={handleFileSelect}
//                   disabled={isUploading}
//                 />
//                 <label htmlFor="file-input">
//                   <Button
//                     variant="contained"
//                     color="primary"
//                     component="span"
//                     style={{ marginTop: "10px" }}
//                     disabled={isUploading}
//                   >
//                     {isUploading ? "Uploading..." : "Choose File"}
//                   </Button>
//                 </label>
//               </Paper>

//               {file && (
//                 <Typography variant="body2">
//                   Selected file: <strong>{file.name}</strong>
//                 </Typography>
//               )}

//               {uploadResult && (
//                 <div style={{ marginTop: "20px" }}>
//                   <Typography variant="body2">
//                     <strong>Status:</strong> {uploadResult.status}
//                   </Typography>
//                   <Typography variant="body2">
//                     <strong>Total Records:</strong> {uploadResult.total_records}
//                   </Typography>
//                   <Typography variant="body2">
//                     <strong>Saved Records:</strong> {uploadResult.saved_records}
//                   </Typography>
//                   <Typography variant="body2">
//                     <strong>Skipped Duplicates:</strong> {uploadResult.skipped_duplicates}
//                   </Typography>
//                 </div>
//               )}
//             </CardBody>
//           </Card>
//         </GridItem>
//       </GridContainer>

//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={5000}
//         onClose={handleCloseSnackbar}
//       >
//         <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </div>
//   );
// }